import queryString, { ParsedQuery } from 'query-string';
import _ from 'lodash';
import Fuse from "fuse.js";
import { Purchases } from '../constants/Purchases';
import WithPriceDecorator from '../decorators/WithPriceDecorator';
import WithTariffPackageDecorator from '../decorators/WithTariffPackageDecorator';
import { IProduct, IProductCharacteristic, ITpackage } from '../entities/product/types';
import Validator from '../validators/Validator';
import { SEARCH_OPTIONS } from "../constants/SearchOptions";


export const filterState = (products: IProduct[]) => {
  const urlParams: ParsedQuery<any> = queryString.parse(window.location.search);
  if (products.length > 0 && Object.keys(urlParams).length) {
    const filteredDevices: any = [];

    let newProducts = [...products];
    if (urlParams.search) {
      newProducts = searchProducts(products, urlParams.search);
    }

    newProducts.forEach((product: any) => {
      const filtersSuccess: any = {
        matched: true,
        filtersSuccess: true,
      };

      filterByTp(product, urlParams.tpackage, urlParams.cosmosInstallments, filtersSuccess);

      filterByBrand(product, urlParams.brand, filtersSuccess);

      filterByOpSystems(product, urlParams.opsystems, filtersSuccess);

      filterByMemorySize(product.productCharacteristics, urlParams.ramMemory, filtersSuccess);

      filterByScreenSize(product.productCharacteristics, urlParams.screenSize, filtersSuccess);

      filterBooleanStringsFilters(product.productCharacteristics, urlParams.dualSim, filtersSuccess, 'ch_dual_sim');

      filterBooleanStringsFilters(product.productCharacteristics, urlParams.smart, filtersSuccess, 'ch_smart_tv');

      filterBooleanStringsFilters(product.productCharacteristics, urlParams.eSim, filtersSuccess, 'ch_esim');

      filterByGroup(product.productGroup, urlParams.group, filtersSuccess);

      filterByColor(product.productColorsImages.colors, urlParams.color, filtersSuccess);

      filterByCategory(product.productLine.name, urlParams.category, filtersSuccess);

      filterByResolution(product.productCharacteristics, urlParams.resolution, filtersSuccess);

      filterBooleanStringsFilters(product.productCharacteristics, urlParams.android, filtersSuccess, 'ch_android');

      filterByDigitalTuner(product.productCharacteristics, urlParams.digitalTuner, filtersSuccess);

      filterByScreenDiagonal(product.productCharacteristics, urlParams.screenDiagonal, filtersSuccess);

      filterByOutlet(product.outlet, urlParams.outlet, filtersSuccess);

      if (filtersSuccess.matched && filtersSuccess.filtersSuccess) {
        filteredDevices.push(product);
      }
    });

    const onlyDevicesWithPrice = removeDeviceWithoutPrice(urlParams.purchase, filteredDevices);

    if (typeof urlParams.orderBy !== 'undefined') {
      return sortDevices(urlParams.orderBy, urlParams.purchase, onlyDevicesWithPrice);
    }

    return onlyDevicesWithPrice;
  }

  return products;
};

export const searchProducts = (products: IProduct[], searchKeyword: string) => {
  if (searchKeyword && searchKeyword.length >= 3) {
    const searchableProducts = products.map((product: IProduct) => {
      return {
        ...product,
        // we create a custom property so that we can search by it later and match it more efficiently
        combined: `${product.manufacturer.name} ${product.model}`,
      };
    });

    const fuse = new Fuse(searchableProducts, SEARCH_OPTIONS);
    return fuse.search(searchKeyword).map(result => result.item);
  }
  return products;
};

const filterByTp = (product: any, tpParam: string, installments: string, filtersSuccess: any): void => {
  if (tpParam !== undefined || installments !== undefined) {
    const tariffFound = product.tariffPackages.some((tariffPackage: ITpackage) => {
      const isTpMatch = Number(tariffPackage.id) === Number(tpParam);
      const installmentsFound = tariffPackage.prices.cosmos.find(
        (price: any) => Number(price.installments) === Number(installments)
      );
      const defaultTp = tariffPackage.id === product.assignedTariffPackage.id;

      let condition = false;

      if (tpParam !== undefined && installments !== undefined) {
        condition = isTpMatch && !_.isEmpty(installmentsFound);
      } else if (installments !== undefined) {
        condition = !_.isEmpty(installmentsFound) && defaultTp;
      } else {
        condition = isTpMatch;
      }

      if (condition) {
        const productPricesDecorator = new WithPriceDecorator(product);
        const productWithTariffPackageDecorator = new WithTariffPackageDecorator(product);
        productPricesDecorator.setPrices(tariffPackage.prices);
        productWithTariffPackageDecorator.setTpData(tariffPackage);
        filtersSuccess.filtersSuccess = true;
        filtersSuccess.matched = true;
        return true;
      }

      return false;
    });

    filtersSuccess.filtersSuccess = tariffFound;
    filtersSuccess.matched = tariffFound;
  }
};

const filterByBrand = (product: any, brandParam: string, filtersSuccess: any): boolean | undefined => {
  if (typeof brandParam !== 'undefined') {
    const brands = brandParam.split('_');
    if (brands.indexOf(product.manufacturer.name) !== -1) {
      filtersSuccess.matched = true;
      return filtersSuccess.matched;
    }
    filtersSuccess.filtersSuccess = false;
    return filtersSuccess.filtersSuccess;
  }
};

const filterByOpSystems = (product: any, opSystemParam: string, filtersSuccess: any): boolean | undefined => {
  if (typeof opSystemParam !== 'undefined') {
    const opsystems = opSystemParam.split('_');
    let flag = false;
    opsystems.forEach((opsystem: string) => {
      product.productCharacteristics.forEach((char: any) => {
        if (char.value.includes(opsystem)) {
          flag = true;
        }
      });
    });

    if (flag) {
      filtersSuccess.matched = true;
      return filtersSuccess.matched;
    }
    filtersSuccess.filtersSuccess = false;
    return filtersSuccess.filtersSuccess;
  }
};

const filterByMemorySize = (
  productCharacteristics: IProductCharacteristic[],
  ramMemoryParam: string,
  filtersSuccess: any
): boolean | undefined => {
  if (!Validator.isUndefined(ramMemoryParam)) {
    const ramMemoryParams = ramMemoryParam.split('_');
    let flag = false;
    let specificChar = getSpecificCharByIdentifier('ch_memory', productCharacteristics);
    specificChar = Validator.isUndefined(specificChar) ? NaN : parseInt(specificChar.value, 10) / 1000;

    ramMemoryParams.forEach((item: string) => {
      const ramMemoryNumber = Number(item.replace(/\D/g, ''));

      if (specificChar === ramMemoryNumber) {
        flag = true;
      }

      if (item.indexOf('<') > -1 && specificChar <= ramMemoryNumber) {
        flag = true;
      }

      if (item.indexOf('>') > -1 && specificChar >= ramMemoryNumber) {
        flag = true;
      }
    });

    if (flag) {
      filtersSuccess.matched = true;
      return filtersSuccess.matched;
    }

    filtersSuccess.filtersSuccess = false;
    return filtersSuccess.filtersSuccess;
  }
};
const filterByScreenSize = (
  productCharacteristics: IProductCharacteristic[],
  screenSizeParam: string,
  filtersSuccess: any
): boolean | undefined => {
  if (!Validator.isUndefined(screenSizeParam)) {
    const screenSizes = screenSizeParam.split('_');
    let flag = false;
    let specificChar = getSpecificCharByIdentifier('ch_screen_size', productCharacteristics);

    if (!Validator.isUndefined(specificChar)) {
      specificChar = parseFloat(specificChar.value);
      screenSizes.forEach((screenSize: string) => {
        const screenSizeNumbers = screenSize.match(/\d+/g);
        const isGreaterCharExists = screenSize.indexOf('>');
        const isLessThanCharExists = screenSize.indexOf('<');

        if (screenSizeNumbers !== null) {
          if (
            screenSizeNumbers.length > 1 &&
            compareMaxChar(specificChar, parseFloat(screenSizeNumbers[0])) &&
            compareMinChar(specificChar, parseFloat(screenSizeNumbers[1]))
          ) {
            flag = true;
          }

          if (screenSizeNumbers.length === 1) {
            if (isGreaterCharExists > -1 && compareMaxChar(specificChar, parseFloat(screenSizeNumbers[0]))) {
              flag = true;
            }

            if (isLessThanCharExists > -1 && compareMinChar(specificChar, parseFloat(screenSizeNumbers[0]))) {
              flag = true;
            }
          }
        }
      });
    }

    if (flag) {
      filtersSuccess.matched = true;
      return filtersSuccess.matched;
    }

    filtersSuccess.filtersSuccess = false;
    return filtersSuccess.filtersSuccess;
  }
};

const filterBooleanStringsFilters = (
  productCharacteristics: IProductCharacteristic[] | IProduct['outlet'],
  clickedParametar: any,
  filtersSuccess: any,
  specificationIdentifier: string
): boolean | undefined => {
  if (typeof clickedParametar !== 'undefined') {
    const mapOfPossibleValue = new Map([
      ['Yes', 'true'],
      ['No', 'false'],
      ['false', 'false'],
      ['true', 'true'],
      ['Ne', 'false'],
      ['Da', 'true'],
    ]);
    const charObj = getSpecificCharByIdentifier(
      specificationIdentifier,
      productCharacteristics as IProductCharacteristic[]
    );
    if (!Validator.isUndefined(charObj)) {
      if (mapOfPossibleValue.has(charObj.value) && mapOfPossibleValue.get(clickedParametar) === charObj.value) {
        filtersSuccess.matched = true;
        return filtersSuccess.matched;
      }
      filtersSuccess.filtersSuccess = false;
      return filtersSuccess.filtersSuccess;
    }
    filtersSuccess.filtersSuccess = false;
    return filtersSuccess.filtersSuccess;
  }
};

const filterBySmart = (
  productCharacteristics: IProductCharacteristic[],
  smartParam: string,
  filtersSuccess: any
): boolean | undefined => {
  if (typeof smartParam !== 'undefined') {
    const mapOfPossibleValue = new Map([
      ['Yes', 'true'],
      ['No', 'false'],
      ['false', 'false'],
      ['true', 'true'],
      ['Ne', 'false'],
      ['Da', 'true'],
    ]);
    const charObj = getSpecificCharByIdentifier('ch_esim', productCharacteristics);
    if (!Validator.isUndefined(charObj)) {
      if (mapOfPossibleValue.has(charObj.value) && mapOfPossibleValue.get(smartParam) === charObj.value) {
        filtersSuccess.matched = true;
        return filtersSuccess.matched;
      }

      filtersSuccess.filtersSuccess = false;
      return filtersSuccess.filtersSuccess;
    }
    filtersSuccess.filtersSuccess = false;
    return filtersSuccess.filtersSuccess;
  }
};

const filterByGroup = (productGroups: any, groupParam: string, filtersSuccess: any): boolean | undefined => {
  if (typeof groupParam !== 'undefined') {
    if (productGroups !== null && groupParam === productGroups.name) {
      filtersSuccess.matched = true;
      return filtersSuccess.matched;
    }
    filtersSuccess.filtersSuccess = false;
    return filtersSuccess.filtersSuccess;
  }
};

const filterByColor = (colors: any, colorParam: string, filtersSuccess: any) => {
  if (typeof colorParam !== 'undefined') {
    const colorParams = colorParam.split('_');
    colors.some((color: any) => {
      if (colorParams.indexOf(color.label) !== -1) {
        filtersSuccess.filtersSuccess = true;
        filtersSuccess.matched = true;
        return filtersSuccess.matched;
      }
      filtersSuccess.filtersSuccess = false;
      return filtersSuccess.filtersSuccess;
    });
  }
};

const filterByCategory = (categoryName: string, categoryParam: string, filtersSuccess: any) => {
  if (typeof categoryParam !== 'undefined') {
    const categories = categoryParam.split('_');
    if (categories.indexOf(categoryName) !== -1) {
      filtersSuccess.matched = true;
      return filtersSuccess.matched;
    }
    filtersSuccess.filtersSuccess = false;
    return filtersSuccess.filtersSuccess;
  }

  return null;
};

const filtersSuccessCompare = (
  specificationIdentifier: string,
  productCharacteristics: IProductCharacteristic[],
  arrayOfPossibleValue: any,
  clickedParametar: any,
  filtersSuccess: any
): boolean | undefined => {
  if (!Validator.isUndefined(clickedParametar)) {
    const charObj = getSpecificCharByIdentifier(specificationIdentifier, productCharacteristics);
    if (!Validator.isUndefined(charObj)) {
      if (
        arrayOfPossibleValue.includes(charObj.value) &&
        arrayOfPossibleValue.find((element: any) => element === clickedParametar) === charObj.value
      ) {
        filtersSuccess.matched = true;
        return filtersSuccess.matched;
      }
      filtersSuccess.filtersSuccess = false;
      return filtersSuccess.filtersSuccess;
    }
    filtersSuccess.filtersSuccess = false;
    return filtersSuccess.filtersSuccess;
  }
};

const filterByResolution = (
  productCharacteristics: IProductCharacteristic[],
  resolution: string,
  filtersSuccess: any
): boolean | undefined => {
  const arrayOfPossibleValue = ['720p HD Ready', '1080p Full HD', 'Ultra HD', '4k Ultra HD'];
  return filtersSuccessCompare(
    'ch_resolution',
    productCharacteristics,
    arrayOfPossibleValue,
    resolution,
    filtersSuccess
  );
};

const filterByDigitalTuner = (
  productCharacteristics: IProductCharacteristic[],
  digitalTuner: string,
  filtersSuccess: any
): boolean | undefined => {
  const arrayOfPossibleValue = ['DVB-T/C/S2', 'DVB-T/C/S/S2', 'DVB-T/C/T2', 'DVB-T/T2', 'DVB-T/C/S3'];
  return filtersSuccessCompare(
    'ch_digital_tuner',
    productCharacteristics,
    arrayOfPossibleValue,
    digitalTuner,
    filtersSuccess
  );
};

const filterByScreenDiagonal = (
  productCharacteristics: IProductCharacteristic[],
  screenDiagonal: string,
  filtersSuccess: any
): boolean | undefined => {
  const arrayOfPossibleValue = ['24', '32', '39', '40'];
  return filtersSuccessCompare(
    'ch_screen_size',
    productCharacteristics,
    arrayOfPossibleValue,
    screenDiagonal,
    filtersSuccess
  );
};

const filterByOutlet = (isOutlet: IProduct['outlet'], outlet: string, filtersSuccess: any): boolean | undefined => {
  if (!Validator.isUndefined(outlet)) {
    if (isOutlet.toString() === outlet) {
      filtersSuccess.matched = true;
      return filtersSuccess.matched;
    }
    filtersSuccess.filtersSuccess = false;
    return filtersSuccess.filtersSuccess;
  }
};

export const getSpecificCharByIdentifier = (
  identifier: string,
  productCharacteristics: IProductCharacteristic[]
): any => {
  return productCharacteristics.find((el: any) => el.characteristicIdentifier === identifier);
};
const sortDevices = (orderBy: string, purchase: string, products: any[]): any[] => {
  switch (orderBy) {
  case 'price-asc':
    if (purchase === Purchases.COSMOS) {
      return [...products].sort(
        (a, b) => convertPriceToInt(a.prices.displayCreditPrice) - convertPriceToInt(b.prices.displayCreditPrice)
      );
    }
    return [...products].sort((a, b) => {
      return convertPriceToInt(a.prices.displayPrice) - convertPriceToInt(b.prices.displayPrice);
    });
  case 'price-desc':
    if (purchase === Purchases.COSMOS) {
      return [...products]
        .sort(
          (a, b) => convertPriceToInt(a.prices.displayCreditPrice) - convertPriceToInt(b.prices.displayCreditPrice)
        )
        .reverse();
    }
    return [...products]
      .sort((a, b) => {
        return convertPriceToInt(a.prices.displayPrice) - convertPriceToInt(b.prices.displayPrice);
      })
      .reverse();
  case 'name-asc':
    return [...products].sort((a, b) => a.manufacturer.name.localeCompare(b.manufacturer.name));
  case 'name-desc':
    return [...products].sort((a, b) => b.manufacturer.name.localeCompare(a.manufacturer.name));
  }
  return products;
};
export const compareMinChar = (specificChar: number, minValue: number): boolean => {
  if (specificChar <= minValue) {
    return true;
  }
  return false;
};
export const compareMaxChar = (specificChar: number, maxValue: number): boolean => {
  return specificChar >= maxValue;
};

const convertPriceToInt = (price: string) => {
  return parseInt(price.replace(/\./g, ''), 10);
};

const removeDeviceWithoutPrice = (purchase: string, products: any): any[] => {
  const devicesWithPrice: any[] = [];
  products.map((product: any) => {
    if (purchase === Purchases.COSMOS) {
      if (
        typeof product.prices !== 'undefined' &&
        product.prices.displayCreditPrice !== '' &&
        product.prices.displayCreditPrice > 0
      ) {
        return devicesWithPrice.push(product);
      }
    } else if (product.prices.displayPrice && product.prices.displayPrice > 0) {
      return devicesWithPrice.push(product);
    }
  });

  return devicesWithPrice;
};
